import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { formatDate, formatRelative } from "@/lib/format";
import {
    Printer,
    ClipboardList,
    FileText,
    Tags,
    CheckCircle2,
    Check,
    Circle,
    Search,
} from "lucide-react";

/**
 * Printcentrum — overzicht + bulk-printen + status-tracking.
 *
 * Werking:
 *  1. Gebruiker selecteert 1+ orders (checkboxes) en (optioneel) 1 of meerdere
 *     locaties als filter.
 *  2. Klikt op één van de 3 actie-knoppen:
 *       • Raaplijst printen     → 1 gecombineerde A4 raaplijst
 *       • Samenvattingen printen → N samenvattingen onder elkaar
 *       • Labels printen        → alle karren-labels van alle orders concateneerd
 *  3. App navigeert in DEZELFDE tab naar de bijbehorende mooie print-pagina,
 *     met `?print_session=<id>` zodat de banner bovenaan weet welke records hij
 *     moet markeren bij "Markeer als geprint".
 *  4. Op de print-pagina ziet de gebruiker de banner met:
 *       [Markeer als geprint] / [Annuleren] → terug naar /printcentrum
 */

const DOC_TYPES = [
    { key: "picklist", label: "Raaplijst", hint: "Eén werklijst voor de kas", icon: ClipboardList },
    { key: "summary", label: "Samenvattingen", hint: "Een overzicht per bestelling", icon: FileText },
    { key: "stickers", label: "Karstickers", hint: "Een duidelijke sticker per kar", icon: Tags },
];

export default function PrintCenter() {
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [query, setQuery] = useState("");
    const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
    const [selectedLocationIds, setSelectedLocationIds] = useState(new Set());

    const load = async () => {
        setLoading(true);
        setLoadError("");
        try {
            const r = await api.get("/print-center");
            setData(r.data);
        } catch (e) {
            setData(null);
            setLoadError("Printcentrum kon niet worden geladen.");
            toast.error("Printcentrum laden mislukt");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const orders = useMemo(() => data?.orders || [], [data?.orders]);
    const locations = useMemo(() => data?.locations || [], [data?.locations]);

    // Filter orders op locatie-selectie + zoekterm
    const filteredOrders = useMemo(() => {
        let arr = orders;
        if (selectedLocationIds.size > 0) {
            arr = arr.filter((o) =>
                (o.locations || []).some((l) => selectedLocationIds.has(l.id))
            );
        }
        if (query.trim()) {
            const q = query.toLowerCase();
            arr = arr.filter((o) =>
                (o.customer_name || "").toLowerCase().includes(q)
                || (o.delivery_date || "").includes(q)
                || (o.id || "").toLowerCase().includes(q),
            );
        }
        return arr;
    }, [orders, selectedLocationIds, query]);

    const selectedOrders = useMemo(
        () => filteredOrders.filter((o) => selectedOrderIds.has(o.id)),
        [filteredOrders, selectedOrderIds]
    );

    const printLocationIds = Array.from(selectedLocationIds);

    const toggleOrder = (id) => {
        setSelectedOrderIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const toggleAll = () => {
        if (filteredOrders.every((o) => selectedOrderIds.has(o.id)) && filteredOrders.length > 0) {
            setSelectedOrderIds(new Set());
        } else {
            setSelectedOrderIds(new Set(filteredOrders.map((o) => o.id)));
        }
    };
    const toggleLocation = (id) => {
        setSelectedLocationIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
        setSelectedOrderIds(new Set());
    };
    const selectAllLocations = () => {
        setSelectedLocationIds(new Set(locations.map((location) => location.id)));
        setSelectedOrderIds(new Set());
    };
    const clearLocations = () => {
        setSelectedLocationIds(new Set());
        setSelectedOrderIds(new Set());
    };

    /**
     * Start print-flow voor een gegeven doctype.
     * - Bouwt de batch-records (voor "markeer als geprint" na confirm)
     * - Schrijft naar sessionStorage onder `print_session_<id>`
     * - Navigeert in dezelfde tab naar de print-pagina met `?auto_print=1&print_session=<id>`
     */
    const startPrint = (docType) => {
        if (printLocationIds.length === 0) {
            toast.error("Selecteer eerst minstens 1 locatie");
            return;
        }
        if (selectedOrders.length === 0) {
            toast.error("Selecteer eerst minstens 1 bestelling");
            return;
        }
        const sessionId = globalThis.crypto?.randomUUID?.() || `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

        // Filter orders die voor dit doctype geldig zijn
        let validOrders = selectedOrders;
        if (docType === "stickers" || docType === "summary") {
            const staleOrders = selectedOrders.filter((o) => o.is_stale);
            if (staleOrders.length > 0) {
                toast.error(`${staleOrders.length} bestelling${staleOrders.length === 1 ? " is" : "en zijn"} gewijzigd. Bereken deze eerst opnieuw voordat je print.`);
                return;
            }
        }
        if (docType === "stickers") {
            validOrders = selectedOrders.filter((o) => o.last_calculation_id);
            if (validOrders.length === 0) {
                toast.error("Geen van de geselecteerde orders heeft karren berekend. Voer eerst een berekening uit.");
                return;
            }
            if (validOrders.length < selectedOrders.length) {
                toast.warning(`${selectedOrders.length - validOrders.length} order(s) overgeslagen — nog geen karren berekend.`);
            }
        }

        // Houd iedere werkelijk geprinte locatie apart bij. Daardoor blijft de
        // status ook correct wanneer later maar één locatie wordt bekeken.
        const records = validOrders.flatMap((o) =>
            (o.locations || [])
                .filter((location) => selectedLocationIds.has(location.id))
                .map((location) => ({
                    order_id: o.id,
                    location_id: location.id,
                    doc_type: docType,
                    order_updated_at: o.updated_at,
                    calculation_id: docType === "stickers" || docType === "summary"
                        ? (o.last_calculation_id || "")
                        : "",
                }))
        );

        const docTypeLabel = DOC_TYPES.find((d) => d.key === docType)?.label || docType;
        const selectedCodes = locations
            .filter((location) => selectedLocationIds.has(location.id))
            .map((location) => location.code)
            .join(", ");
        const locLabel = ` · locatie${printLocationIds.length === 1 ? "" : "s"} ${selectedCodes}`;
        const label = `${docTypeLabel} van ${validOrders.length} bestelling${validOrders.length === 1 ? "" : "en"}${locLabel}`;

        try {
            sessionStorage.setItem(
                `print_session_${sessionId}`,
                JSON.stringify({ records, label, count: records.length })
            );
        } catch (err) {
            // sessionStorage kan vol/uit staan — flow werkt alleen niet voor de markeer-stap
            console.warn("[printcentrum] sessionStorage write failed", err);
        }

        // Bouw doel-URL
        const ids = validOrders.map((o) => o.id).join(",");
        const qs = new URLSearchParams();
        qs.set("ids", ids);
        qs.set("location_ids", printLocationIds.join(","));
        qs.set("auto_print", "1");
        qs.set("print_session", sessionId);

        let url;
        if (docType === "picklist") {
            url = `/raaplijst/gezamenlijk?${qs.toString()}`;
        } else if (docType === "summary") {
            url = `/printcentrum/samenvattingen?${qs.toString()}`;
        } else {
            url = `/printcentrum/labels?${qs.toString()}`;
        }
        nav(url);
    };

    return (
        <div className="p-5 sm:p-7 lg:p-10 xl:p-12 max-w-[1480px] mx-auto pb-24 lg:pb-12" data-testid="print-center-page">
            <PageHeader
                kicker="Printcentrum"
                title="Centraal printen"
                subtitle="Kies één of meer locaties, selecteer de bestellingen en bepaal wat je wilt printen. In drie overzichtelijke stappen."
                testid="print-center-header"
            />

            <section className="bg-white border border-border rounded-2xl p-5 sm:p-6 mb-5 shadow-sm" data-testid="print-loc-filter">
                <div className="flex items-start gap-4 mb-5">
                    <StepNumber>1</StepNumber>
                    <div>
                        <h2 className="font-semibold text-ink-950">Kies één of meer locaties</h2>
                        <p className="text-sm text-ink-500 mt-0.5">Vink precies de locaties aan die je samen in één printopdracht wilt opnemen.</p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        onClick={selectAllLocations}
                        data-testid="loc-all"
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                            locations.length > 0 && selectedLocationIds.size === locations.length
                                ? "bg-ink-950 border-ink-950 text-white"
                                : "bg-white border-border text-ink-700 hover:bg-ink-50"
                        }`}
                    >
                        <Check className="h-3.5 w-3.5" /> Alles selecteren
                    </button>
                    <button
                        onClick={clearLocations}
                        disabled={selectedLocationIds.size === 0}
                        data-testid="loc-clear"
                        className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium border border-border bg-white text-ink-600 hover:bg-ink-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Selectie wissen
                    </button>
                    {locations.map((l) => {
                        const on = selectedLocationIds.has(l.id);
                        const count = orders.filter((o) => (o.locations || []).some((lo) => lo.id === l.id)).length;
                        return (
                            <button
                                key={l.id}
                                onClick={() => toggleLocation(l.id)}
                                data-testid={`loc-toggle-${l.code}`}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                                    on
                                        ? "border-florefy-800 ring-2 ring-florefy-200"
                                        : "border-border hover:bg-ink-50"
                                }`}
                                style={{
                                    backgroundColor: on ? (l.color || "#94a3b8") + "33" : undefined,
                                }}
                            >
                                <span className="inline-flex h-4 w-4 items-center justify-center rounded border border-black/20" style={{ backgroundColor: on ? (l.color || "#94a3b8") : "white" }}>
                                    {on && <Check className="h-3 w-3 text-ink-950" />}
                                </span>
                                {l.code}
                                <span className="font-mono text-[10px] text-ink-500">·{count}</span>
                            </button>
                        );
                    })}
                </div>
            </section>

            <section className="mb-5">
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-3">
                    <div className="flex items-start gap-4">
                        <StepNumber>2</StepNumber>
                        <div>
                            <h2 className="font-semibold text-ink-950">Selecteer bestellingen</h2>
                            <p className="text-sm text-ink-500 mt-0.5" data-testid="selection-count">{selectedOrders.length} van {filteredOrders.length} geselecteerd</p>
                        </div>
                    </div>
                    <div className="relative w-full sm:max-w-sm" data-testid="print-search-wrap">
                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-ink-400" />
                <input
                    type="text"
                    placeholder="Zoek op klant, datum of order-id…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="w-full pl-8 pr-3 py-2.5 text-sm border border-border rounded-xl bg-white focus:border-florefy-700 focus:outline-none"
                    data-testid="print-search-input"
                />
                    </div>
                </div>

                <section
                    className="sticky top-16 lg:top-4 z-30 mb-4 rounded-2xl border border-florefy-200 bg-white/95 p-3 sm:p-4 shadow-lg shadow-florefy-950/10 backdrop-blur"
                    data-testid="print-actions"
                >
                    <div className="flex flex-col xl:flex-row xl:items-center gap-3 xl:gap-5">
                        <div className="flex items-center gap-3 xl:min-w-[250px]">
                            <StepNumber>3</StepNumber>
                            <div>
                                <h2 className="text-sm font-semibold text-ink-900">Print geselecteerde bestellingen</h2>
                                <p className="text-xs text-ink-500 mt-0.5">
                                    {selectedLocationIds.size === 0
                                        ? "Kies eerst één of meer locaties."
                                        : selectedOrders.length === 0
                                            ? "Selecteer minimaal één bestelling."
                                            : `${selectedOrders.length} bestelling${selectedOrders.length === 1 ? "" : "en"} klaar.`}
                                </p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 flex-1">
                            {DOC_TYPES.map((doc) => (
                                <ActionButton
                                    key={doc.key}
                                    icon={doc.icon}
                                    label={doc.label}
                                    hint={doc.hint}
                                    onClick={() => startPrint(doc.key)}
                                    disabled={selectedOrders.length === 0 || selectedLocationIds.size === 0}
                                    testid={`btn-print-${doc.key === "summary" ? "summaries" : doc.key === "stickers" ? "labels" : doc.key}`}
                                />
                            ))}
                        </div>
                    </div>
                </section>

            {/* Tabel */}
            {loading ? (
                <div className="text-sm text-ink-500 p-8 text-center">Laden…</div>
            ) : loadError ? (
                <div className="text-sm text-red-800 p-5 text-center bg-red-50 border border-red-200 rounded-2xl" role="alert">
                    {loadError}
                    <button type="button" onClick={load} className="ml-2 font-semibold underline">Opnieuw proberen</button>
                </div>
            ) : filteredOrders.length === 0 ? (
                <div className="text-sm text-ink-500 p-8 text-center bg-white border border-border rounded-2xl">
                    {orders.length === 0
                        ? "Geen bestellingen gevonden."
                        : "Geen resultaten voor je filter/zoekopdracht."}
                </div>
            ) : (
                <div className="bg-white border border-border rounded-2xl overflow-x-auto shadow-sm" data-testid="print-orders-table">
                    <table className="w-full text-sm">
                        <thead className="bg-ink-50 text-ink-700 border-b border-border">
                            <tr>
                                <th className="w-10 px-3 py-2.5 text-left">
                                    <input
                                        type="checkbox"
                                        checked={
                                            filteredOrders.length > 0
                                            && filteredOrders.every((o) => selectedOrderIds.has(o.id))
                                        }
                                        onChange={toggleAll}
                                        data-testid="toggle-select-all"
                                        className="h-3.5 w-3.5"
                                    />
                                </th>
                                <th className="px-3 py-2.5 text-left font-medium">Klant · Leverdatum</th>
                                <th className="px-3 py-2.5 text-left font-medium">Locaties</th>
                                {DOC_TYPES.map((d) => (
                                    <th key={d.key} className="px-3 py-2.5 text-left font-medium">
                                        {d.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredOrders.map((o) => (
                                <PrintRow
                                    key={o.id}
                                    order={o}
                                    selectedLocationIds={selectedLocationIds}
                                    selected={selectedOrderIds.has(o.id)}
                                    onToggle={() => toggleOrder(o.id)}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            </section>

        </div>
    );
}

function StepNumber({ children }) {
    return <span className="h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 bg-florefy-100 text-florefy-900">{children}</span>;
}

function ActionButton({ icon: Icon, label, hint, onClick, disabled, testid }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            data-testid={testid}
            className="group flex items-center gap-3 text-left px-3.5 py-3 rounded-xl border border-florefy-900 bg-florefy-900 text-white shadow-sm transition-all hover:bg-florefy-800 hover:border-florefy-800 disabled:border-ink-200 disabled:bg-ink-100 disabled:text-ink-400 disabled:shadow-none disabled:cursor-not-allowed"
        >
            <span className="h-9 w-9 rounded-lg bg-white/10 group-disabled:bg-white/60 flex items-center justify-center shrink-0"><Icon className="h-5 w-5" /></span>
            <span><span className="block text-sm font-semibold">{label}</span><span className="block text-xs opacity-65 mt-0.5">{hint}</span></span>
        </button>
    );
}

function PrintRow({ order, selected, selectedLocationIds, onToggle }) {
    const relevantLocationIds = (order.locations || [])
        .map((location) => location.id)
        .filter((locationId) => selectedLocationIds.has(locationId));
    return (
        <tr
            className={`border-b border-border last:border-0 ${selected ? "bg-florefy-50/40" : "hover:bg-ink-50"}`}
            data-testid={`print-row-${order.id}`}
        >
            <td className="px-3 py-2.5 align-top">
                <input
                    type="checkbox"
                    checked={selected}
                    onChange={onToggle}
                    className="h-3.5 w-3.5"
                    data-testid={`select-order-${order.id}`}
                />
            </td>
            <td className="px-3 py-2.5 align-top">
                <Link
                    to={`/bestellingen/${order.id}`}
                    className="font-medium text-ink-900 hover:text-florefy-700"
                    data-testid={`row-customer-${order.id}`}
                >
                    {order.customer_name}
                </Link>
                <div className="text-xs text-ink-500 mt-0.5">
                    {formatDate(order.delivery_date)} · {order.status}
                </div>
                {order.is_stale && (
                    <div className="mt-1 inline-flex rounded-md border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                        Gewijzigd · opnieuw berekenen
                    </div>
                )}
            </td>
            <td className="px-3 py-2.5 align-top">
                <div className="flex flex-wrap gap-1">
                    {order.locations.length === 0 ? (
                        <span className="text-xs text-ink-400 italic">—</span>
                    ) : (
                        order.locations.map((l) => (
                            <span
                                key={l.id}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono font-medium"
                                style={{ backgroundColor: (l.color || "#94a3b8") + "33", color: "#0f172a" }}
                                title={l.name}
                            >
                                {l.code}
                            </span>
                        ))
                    )}
                </div>
            </td>
            {DOC_TYPES.map((d) => {
                const locationStates = relevantLocationIds.map(
                    (locationId) => order.print_state_per_location?.[locationId]?.[d.key] || null
                );
                const printedStates = locationStates.filter(Boolean);
                let s = null;
                if (printedStates.length === locationStates.length && printedStates.length > 0) {
                    s = printedStates.reduce((latest, current) =>
                        !latest || current.printed_at > latest.printed_at ? current : latest
                    , null);
                } else if (printedStates.length > 0) {
                    s = {
                        partial: true,
                        printed_count: printedStates.length,
                        total_count: locationStates.length,
                    };
                }
                const disabled = d.key === "stickers" && (!order.last_calculation_id || order.is_stale);
                return (
                    <td key={d.key} className="px-3 py-2.5 align-top">
                        <PrintStatusCell
                            state={s}
                            disabled={disabled}
                            disabledHint={order.is_stale ? "Bestelling eerst opnieuw berekenen" : "Eerst karren berekenen"}
                            testid={`status-${order.id}-${d.key}`}
                        />
                    </td>
                );
            })}
        </tr>
    );
}

function PrintStatusCell({ state, disabled, disabledHint, testid }) {
    if (disabled) {
        return (
            <span
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-ink-100 text-[11px] text-ink-500"
                data-testid={`${testid}-disabled`}
                title={disabledHint}
            >
                <Circle className="h-3 w-3" /> n.v.t.
            </span>
        );
    }
    if (!state) {
        return (
            <span
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full border border-ink-200 bg-white text-[11px] text-ink-600"
                data-testid={`${testid}-not-printed`}
            >
                <Circle className="h-3 w-3" /> niet geprint
            </span>
        );
    }
    if (state.partial) {
        return (
            <span
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full border border-amber-200 bg-amber-50 text-[11px] font-medium text-amber-800"
                data-testid={`${testid}-partial`}
                title={`${state.printed_count} van ${state.total_count} geselecteerde locaties geprint`}
            >
                <Circle className="h-3 w-3" /> {state.printed_count}/{state.total_count} geprint
            </span>
        );
    }
    return (
        <span
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-50 text-[11px] font-medium text-emerald-700"
            data-testid={`${testid}-printed`}
            title={`Door ${state.printed_by_name || "—"} op ${formatDate(state.printed_at, { withTime: true })}`}
        >
            <CheckCircle2 className="h-3 w-3" /> {formatRelative(state.printed_at)}
        </span>
    );
}
